import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, CardTitle, Col, Input, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { ErrorToaster, successToaster } from "components/common/toast";
import {
  AddPromotionAPI,
  DeletePromotionAPI,
  GetPromotionsAPI,
  SavePromotionAPI,
} from "network/orders";
import { errText } from "views/bookings/Agenda";
import { GetChannelsAPI } from "network/orders";

// Tipi e ambiti: i nomi mostrati sono in orders:promo.kinds.* e orders:promo.scopes.*
const KIND = ["percent", "fixed", "free_delivery", "bogo", "bundle", "bundle_percent"];
const NEEDS_ITEMS = ["bundle", "bundle_percent"];
const SCOPE = ["all", "pickup", "delivery"];
const EMPTY = {
  name: "", kind: "percent", value: "10", applies_to: "all", min_order: "0", first_order_only: false,
  code: "", weekdays: [], start_date: "", end_date: "", buy_qty: 1, items: [],
};

const Promotions = () => {
  const { t } = useTranslation("orders");
  const DAYS = t("days", { returnObjects: true });
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [dishes, setDishes] = useState([]);

  const load = async () => {
    try {
      setList(await GetPromotionsAPI());
    } catch (e) {
      ErrorToaster(errText(e));
    }
  };
  useEffect(() => {
    load();
    GetChannelsAPI().then(setDishes).catch(() => {});
  }, []);
  const run = async (fn, ok) => {
    try {
      await fn();
      if (ok) successToaster(ok);
      load();
      return true;
    } catch (e) {
      ErrorToaster(errText(e));
      return false;
    }
  };
  const save = async () => {
    const body = { ...form, weekdays: form.weekdays };
    const ok = await run(() => (form.id ? SavePromotionAPI(form.id, body) : AddPromotionAPI(body)), t("promo.saved"));
    if (ok) setForm(null);
  };
  const set = (k, v) => setForm({ ...form, [k]: v });
  const setItem = (id, quantity) =>
    set("items", quantity > 0 ? [...form.items.filter((i) => i.id !== id), { id, quantity }] : form.items.filter((i) => i.id !== id));
  const qtyOf = (id) => form.items.find((i) => i.id === id)?.quantity || 0;
  const edit = (p) => setForm({ ...p, start_date: p.start_date || "", end_date: p.end_date || "" });

  return (
    <>
      <Card>
        <CardBody>
          <CardTitle tag="h4">{t("promo.title")}</CardTitle>
          <p className="text-muted">
            {t("promo.intro")}
          </p>
          {list.length === 0 && <p className="text-muted">{t("promo.none")}</p>}
          {list.map((p) => (
            <Row key={p.id} className="align-items-center py-2 border-bottom">
              <Col md="3">
                <strong>{p.name}</strong>
                {!p.active && <span className="badge badge-secondary ml-2">{t("promo.off")}</span>}
              </Col>
              <Col md="4">
                {p.text} · {t(`promo.scopes.${p.applies_to}`, { defaultValue: "" })}
                {p.first_order_only && ` · ${t("promo.firstOrder")}`}
                {p.code && <> · {t("promo.codeWord")} <code>{p.code}</code></>}
              </Col>
              <Col md="3" className="small text-muted">
                {p.weekdays.length > 0 && p.weekdays.map((d) => DAYS[d]).join(" ")}
                {(p.start_date || p.end_date) && ` ${p.start_date || "…"} → ${p.end_date || "…"}`}
              </Col>
              <Col md="2" className="text-md-right">
                <Button size="sm" onClick={() => run(() => SavePromotionAPI(p.id, { active: !p.active }))}>{p.active ? t("promo.turnOff") : t("promo.turnOn")}</Button>
                <Button size="sm" onClick={() => edit(p)}>{t("promo.edit")}</Button>
                <Button size="sm" color="danger" onClick={() => window.confirm(t("promo.deleteConfirm")) && run(() => DeletePromotionAPI(p.id))}>×</Button>
              </Col>
            </Row>
          ))}
          {!form && <Button color="primary" className="mt-3" onClick={() => setForm({ ...EMPTY })}>{t("promo.new")}</Button>}
        </CardBody>
      </Card>

      {form && (
        <Card>
          <CardBody>
            <CardTitle tag="h4">{form.id ? t("promo.editTitle") : t("promo.newTitle")}</CardTitle>
            <Row>
              <Col md="4" className="mb-3"><label>{t("promo.name")}</label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("promo.namePlaceholder")} /></Col>
              <Col md="4" className="mb-3">
                <label>{t("promo.type")}</label>
                <Input type="select" value={form.kind} onChange={(e) => set("kind", e.target.value)}>
                  {KIND.map((k) => <option key={k} value={k}>{t(`promo.kinds.${k}`)}</option>)}
                </Input>
              </Col>
              {form.kind === "bogo" && (
                <Col md="4" className="mb-3">
                  <label>{t("promo.bogoEvery")}</label>
                  <Input type="select" value={form.buy_qty} onChange={(e) => set("buy_qty", e.target.value)}>
                    {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{t("promo.bogoOption", { n, next: n + 1 })}</option>)}
                  </Input>
                </Col>
              )}
              {!["free_delivery", "bogo"].includes(form.kind) && (
                <Col md="4" className="mb-3">
                  <label>{["percent", "bundle_percent"].includes(form.kind) ? t("promo.percentage") : form.kind === "bundle" ? t("promo.menuPrice") : t("promo.amount")}</label>
                  <Input type="number" min="0" step="0.5" value={form.value} onChange={(e) => set("value", e.target.value)} />
                </Col>
              )}
              <Col md="4" className="mb-3">
                <label>{t("promo.appliesTo")}</label>
                <Input type="select" value={form.applies_to} onChange={(e) => set("applies_to", e.target.value)}>
                  {SCOPE.filter((k) => !(form.kind === "free_delivery" && k !== "delivery")).map((k) => <option key={k} value={k}>{t(`promo.scopes.${k}`)}</option>)}
                </Input>
              </Col>
              <Col md="4" className="mb-3"><label>{t("promo.minOrder")}</label><Input type="number" min="0" value={form.min_order} onChange={(e) => set("min_order", e.target.value)} /></Col>
              <Col md="4" className="mb-3"><label>{t("promo.code")}</label><Input value={form.code} onChange={(e) => set("code", e.target.value)} placeholder={t("promo.codePlaceholder")} /></Col>
              <Col md="3" className="mb-3"><label>{t("promo.from")}</label><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} /></Col>
              <Col md="3" className="mb-3"><label>{t("promo.to")}</label><Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} /></Col>
              <Col md="6" className="mb-3 pt-4">
                <label><input type="checkbox" checked={form.first_order_only} onChange={(e) => set("first_order_only", e.target.checked)} /> {t("promo.firstTimeOnly")}</label>
              </Col>
            </Row>
            {(form.kind === "bogo" || NEEDS_ITEMS.includes(form.kind)) && (
              <div className="mb-3">
                <strong>
                  {form.kind === "bogo" ? t("promo.validDishes") : t("promo.bundleDishes")}
                </strong>
                <div className="promo-dishes mt-2" style={{ maxHeight: 220, overflowY: "auto" }}>
                  {dishes.map((d) => (
                    <div key={d.id} className="d-flex align-items-center py-1">
                      {form.kind === "bogo" ? (
                        <label className="mb-0"><input type="checkbox" checked={qtyOf(d.id) > 0} onChange={(e) => setItem(d.id, e.target.checked ? 1 : 0)} /> {d.name}</label>
                      ) : (
                        <>
                          <Input type="number" min="0" max="20" value={qtyOf(d.id)} onChange={(e) => setItem(d.id, Number(e.target.value))} style={{ width: 70 }} className="mr-2" />
                          <span>{d.name} <span className="text-muted">{d.price} €</span></span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mb-3">
              <strong className="mr-2">{t("promo.days")}</strong>
              {DAYS.map((d, i) => (
                <label key={d} className="mr-3">
                  <input type="checkbox" checked={form.weekdays.includes(i)}
                    onChange={() => set("weekdays", form.weekdays.includes(i) ? form.weekdays.filter((x) => x !== i) : [...form.weekdays, i])} /> {d}
                </label>
              ))}
            </div>
            <Button color="primary" onClick={save}>{t("save")}</Button>
            <Button onClick={() => setForm(null)}>{t("cancel")}</Button>
          </CardBody>
        </Card>
      )}
    </>
  );
};

export default Promotions;
